import React from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Divider,
  Space,
  Table,
  Typography,
  Upload,
} from "antd";
import { PlusOutlined, SaveOutlined, InboxOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const CashFormLayout = ({
  form,
  isEditMode,
  loading,
  invoiceItems,
  partners,
  columns,
  calculatedValues,
  onFinish,
  handlePartnerChange,
  addItem,
  navigate,
  selectedBrand,
  brands = [],
  allBrands = [],
  onBrandChange,
  onValuesChange,
  formTitle = "Create Cash Payment",
}) => {
  // Get the current currency value from the form with a space after it for better formatting
  const getCurrencySymbol = () => (form.getFieldValue("currency") || "$") + " ";

  return (
    <Card title={isEditMode ? "Edit Cash Payment" : "Create Cash Payment"}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        initialValues={{
          cashType: "cash",
          issueDate: dayjs(),
          currency: "$",
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="cashType"
              label="Type"
              rules={[
                { required: true, message: "Please select cash payment type" },
              ]}
            >
              <Select>
                <Option value="cash">Cash</Option>
                <Option value="check">Check</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="number"
              label="Reference Number"
              rules={[
                { required: true, message: "Please enter reference number" },
              ]}
            >
              <Input placeholder="e.g. CASH-2025-001" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="currency" label="Currency">
              <Input placeholder="e.g. $, €, £" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="partner"
              label={
                <Space>
                  <span>Partner</span>
                  <Button
                    type="primary"
                    style={{
                      fontSize: "10px",
                      padding: "0 6px",
                      height: "20px",
                    }}
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      navigate("/accounting/settings", {
                        state: { activeTab: "3" },
                      })
                    }
                  >
                    Add
                  </Button>
                </Space>
              }
              rules={[{ required: true, message: "Please select a partner" }]}
            >
              <Select
                placeholder="Select a partner"
                onChange={handlePartnerChange}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {partners.map((partner) => (
                  <Option key={partner.id} value={partner.id}>
                    {partner.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="brand"
              label="Brand"
              rules={[{ required: true, message: "Please select a brand" }]}
              tooltip="Only brands assigned to the selected partner will be shown"
            >
              <Select
                placeholder="Select a brand"
                showSearch
                onChange={onBrandChange}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                disabled={!brands || brands.length === 0}
              >
                {brands.map((brand) => (
                  <Option key={brand.id} value={brand.id}>
                    {brand.brand_name || brand.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="issueDate"
              label="Issue Date"
              rules={[{ required: true, message: "Please select issue date" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="total_amount"
              label="Total Amount"
              rules={[{ required: true, message: "Please enter total amount" }]}
            >
              <Input
                type="number"
                placeholder="0.00"
                addonBefore={form.getFieldValue("currency") || "$"}
                onWheel={(e) => e.target.blur()}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Cash Payment Items</Divider>

        <Button
          type="dashed"
          onClick={addItem}
          style={{ marginBottom: 16 }}
          icon={<PlusOutlined />}
        >
          Add Item
        </Button>

        <Table
          columns={columns}
          dataSource={invoiceItems}
          pagination={false}
          rowKey="key"
          size="small"
        />

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <div
              style={{
                padding: "8px 16px",
                background: "#f5f5f5",
                borderRadius: 4,
              }}
            >
              <Row justify="space-between">
                <Col>Subtotal:</Col>
                <Col>
                  {getCurrencySymbol()}
                  {parseFloat(calculatedValues.calculated_subtotal || 0)}
                </Col>
              </Row>
              <Row justify="space-between">
                <Col>Tax:</Col>
                <Col>
                  {getCurrencySymbol()}
                  {parseFloat(calculatedValues.calculated_tax_amount || 0)}
                </Col>
              </Row>
              <Row justify="space-between">
                <Col>Discount:</Col>
                <Col>
                  {getCurrencySymbol()}
                  {parseFloat(calculatedValues.calculated_discount_amount || 0)}
                </Col>
              </Row>
              <Divider style={{ margin: "8px 0" }} />
              <Row justify="space-between">
                <Col>
                  <strong>Total:</strong>
                </Col>
                <Col>
                  <strong>
                    {getCurrencySymbol()}
                    {calculatedValues.calculated_total}
                  </strong>
                </Col>
              </Row>
              <Row justify="space-between">
                <Col>
                  <strong
                    style={{
                      color:
                        calculatedValues.calculated_total !==
                        parseFloat(form.getFieldValue("total_amount"))
                          ? "red"
                          : "",
                    }}
                  >
                    {calculatedValues.calculated_total !==
                    parseFloat(form.getFieldValue("total_amount"))
                      ? "Total is not matching to total amount"
                      : " "}
                  </strong>
                </Col>
              </Row>
            </div>
          </Col>

          {/* Editable form fields for manual adjustment */}
          <Col xs={24} sm={12} md={16}>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="subtotal"
                  label="Subtotal"
                  rules={[{ required: false }]}
                >
                  <Input
                    type="number"
                    placeholder="0.00"
                    addonBefore={form.getFieldValue("currency") || "$"}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="tax_rate"
                  label="Tax Rate (%)"
                  rules={[{ required: false }]}
                >
                  <Input
                    type="number"
                    placeholder="0.00"
                    addonAfter="%"
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="discount_amount"
                  label="Discount Amount"
                  rules={[{ required: false }]}
                >
                  <Input
                    type="number"
                    placeholder="0.00"
                    addonBefore={form.getFieldValue("currency") || "$"}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="total_amount"
                  label="Total Amount"
                  rules={[{ required: false }]}
                >
                  <Input
                    type="number"
                    placeholder="0.00"
                    addonBefore={form.getFieldValue("currency") || "$"}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <Divider />

        <Form.Item name="notes" label="Notes">
          <TextArea rows={4} placeholder="Enter any additional notes here" />
        </Form.Item>

        {/* File URL display - separate from the upload component */}
        {form.getFieldValue("fileUrl") && (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                padding: "12px",
                border: "1px solid #d9d9d9",
                borderRadius: "2px",
                marginBottom: "8px",
                background: "#f5f5f5",
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>Current File:</strong>
              </p>
              <a
                href={form.getFieldValue("fileUrl")}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "#1890ff",
                }}
              >
                <svg
                  viewBox="64 64 896 896"
                  focusable="false"
                  data-icon="file-pdf"
                  width="1em"
                  height="1em"
                  fill="currentColor"
                  aria-hidden="true"
                  style={{ marginRight: "8px" }}
                >
                  <path d="M531.3 574.4l.3-1.4c5.8-23.9 13.1-53.7 7.4-80.7-3.8-21.3-19.5-29.6-32.9-30.2-15.8-.7-29.9 8.3-33.4 21.4-6.6 24-.7 56.8 10.1 98.6-13.6 32.4-35.3 79.5-51.2 107.5-29.6 15.3-69.3 38.9-75.2 68.7-1.2 5.5.2 12.5 3.5 18.8 3.7 7 9.6 12.4 16.5 15 3 1.1 6.6 2 10.8 2 17.6 0 46.1-14.2 84.1-79.4 5.8-1.9 11.8-3.9 17.6-5.9 27.2-9.2 55.4-18.8 80.9-23.1 28.2 15.1 60.3 24.8 82.1 24.8 21.6 0 30.1-12.8 33.3-20.5 5.6-13.5 2.9-30.5-6.2-39.6-13.2-13-45.3-16.4-95.3-10.2-24.6-15-40.7-35.4-52.4-65.8zM421.6 726.3c-13.9 20.2-24.4 30.3-30.1 34.7 6.7-12.3 19.8-25.3 30.1-34.7zm87.6-235.5c5.2 8.9 4.5 35.8.5 49.4-4.9-19.9-5.6-48.1-2.7-51.4.8.1 1.5.7 2.2 2zm-1.6 120.5c10.7 18.5 24.2 34.4 39.1 46.2-21.6 4.9-41.3 13-58.9 20.2-4.2 1.7-8.3 3.4-12.3 5 13.3-24.1 24.4-51.4 32.1-71.4zm155.6 65.5c.1.2.2.5-.4.9h-.2c-14.6.8-41.2-4.7-68.7-18.5 27.7-5.3 47.8-4.8 57.4-1.1 4.2 1.6 8.2 5 11.9 18.7z" />
                  <path d="M854.6 288.6L639.4 73.4c-6-6-14.1-9.4-22.6-9.4H192c-17.7 0-32 14.3-32 32v832c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V311.3c0-8.5-3.4-16.7-9.4-22.7zM790.2 326H602V137.8L790.2 326zm1.8 562H232V136h302v216a42 42 0 0042 42h216v494z" />
                </svg>
                {form.getFieldValue("fileUrl").split("/").pop()}
              </a>
            </div>
            <p style={{ marginTop: 8 }}>
              Upload a new file to replace the current one:
            </p>
          </div>
        )}

        {/* File upload component */}
        <Form.Item
          name="file"
          label="Upload Cash Payment File"
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e?.fileList;
          }}
        >
          <Upload.Dragger
            name="file"
            beforeUpload={() => false}
            maxCount={1}
            accept=".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for a single file upload. Accepted formats: PDF, CSV,
              Excel, Images.
            </p>
          </Upload.Dragger>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
            >
              {isEditMode ? "Update Cash Payment" : "Create Cash Payment"}
            </Button>
            <Button onClick={() => navigate("/accounting/cash")}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default CashFormLayout;
